import { pendingScanRepository } from '@/db/repositories/pendingScanRepository';
import { scansApi } from '@/shared/api/scansApi';
import type { PendingScan } from '@/db/models';
import type { ScanUploadItem } from '@/shared/types/domain';

// A single request stays a reasonable size even after a long offline period
// (e.g. a full conference day) accumulates a large backlog -- uploading
// everything in one giant request would risk a timeout that then has to be
// retried from scratch. Multiple smaller batches also let the UI's pending
// count tick down progressively rather than jumping once at the very end.
const BATCH_SIZE = 25;

export interface ProcessResult {
  attempted: number;
  accepted: number;
  rejected: number;
  /** True if at least one batch failed outright (network/5xx) and was left for retry. */
  hadTransientFailure: boolean;
  transientFailureMessage: string | null;
}

function toUploadItem(scan: PendingScan): ScanUploadItem {
  return {
    clientLocalId: scan.localId,
    eventId: scan.eventId,
    exhibitorId: scan.exhibitorId,
    scannedValue: scan.scannedValue,
    scannedByUserId: scan.userId,
    scannedAtUtc: scan.scannedAtUtc,
    notes: scan.notes,
    rating: scan.rating
  };
}

function chunk<T>(items: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
}

/**
 * Uploads every currently-pending scan, in batches, reconciling each item's
 * terminal outcome (accepted or definitively rejected) against IndexedDB.
 * Per the Stage 4 backend design, every item in a SUCCESSFUL response is
 * terminal -- `errorCode` present means rejected, absent means accepted --
 * there is no "try again later" outcome for an individual item within a
 * completed response. Only a failed HTTP call for an entire batch (network
 * error, timeout, 5xx) is retriable, and only increments retryCount.
 */
export async function processPendingScans(): Promise<ProcessResult> {
  const pending = await pendingScanRepository.getPending();

  const result: ProcessResult = {
    attempted: 0,
    accepted: 0,
    rejected: 0,
    hadTransientFailure: false,
    transientFailureMessage: null
  };

  if (pending.length === 0) {
    return result;
  }

  for (const batch of chunk(pending, BATCH_SIZE)) {
    // If connectivity drops mid-run (e.g. walking out of Wi-Fi range while a
    // large backlog is uploading), stop rather than let every remaining
    // batch fail one by one against a connection we already know is gone.
    if (!navigator.onLine) {
      break;
    }

    result.attempted += batch.length;

    try {
      const response = await scansApi.upload(batch.map(toUploadItem));

      for (const item of response.results) {
        if (item.errorCode) {
          await pendingScanRepository.markRejected(
            item.clientLocalId,
            item.errorCode,
            item.errorMessage ?? 'This scan was rejected by the server.'
          );
          result.rejected += 1;
        } else if (item.serverScanId) {
          await pendingScanRepository.markUploaded(item.clientLocalId, item.serverScanId);
          result.accepted += 1;
        }
      }
    } catch (err) {
      result.hadTransientFailure = true;
      result.transientFailureMessage = err instanceof Error ? err.message : 'Upload failed.';

      await Promise.all(batch.map((scan) => pendingScanRepository.incrementRetryCount(scan.localId)));

      // Don't attempt subsequent batches in this run -- if the network just
      // failed once, it will very likely fail again immediately after; wait
      // for the next scheduled tick (or the next 'online' event) instead of
      // burning through retries in a tight loop.
      break;
    }
  }

  return result;
}
